/**
 * #258 — Table View: the unified **table-focus** model.
 *
 * One abstraction renders the shared-display surface (the "Table View"). The
 * Table View shows the current *table focus* through a single, designated
 * Viewer's projection. "Player View on the shared screen" and "Pin a map /
 * entity / handout" are two VALUES of one focus pointer, not two features:
 *
 *   focus ∈ { viewer-projection (default) | pinned-entity | pinned-handout }
 *
 * The **designated-Viewer rule** is load-bearing (07-session-experience.md §4):
 * the table carries a GM-set designated Viewer (default = the party Viewer),
 * and EVERY focus value renders through `project(designatedViewer, t)`. Pinning
 * changes WHAT is shown, never WHOSE eyes — a pinned entity page is still
 * reveal-filtered to the designated Viewer, so the table can never show more
 * than that Viewer's projection. The chokepoint (`packages/projection`) is the
 * sole producer of the projected facts the table renders; this module never
 * constructs a `VisibleFact` and re-implements no reveal filter.
 *
 * The focus pointer is **ephemeral presentation state** (like the browse
 * cursor): never persisted, never written to the op log, never synced through
 * the substrate. The GM's main tab owns the single replica, computes the
 * designated-Viewer projection once, and BROADCASTS the already-projected rows
 * plus the focus pointer to a chrome-stripped Table tab via `BroadcastChannel`
 * (transport decision #2). The Table tab opens NO second replica — it is a pure
 * renderer of broadcast values, so there is no OPFS sync-access-handle
 * contention.
 *
 * The union is designed to widen ADDITIVELY: `map` (geography wave) and
 * `hud` / `book` (#225) slot in as new arms without touching the viewer /
 * entity / handout arms or the transport. Out of scope for v1 (do NOT build):
 * §3 power-user multi-monitor configs, a separate authenticated spectator-
 * device transport, selective-render transitions.
 */

import { campaignViewers, type EntityState, type ViewerId } from '@dl/shared';

/**
 * The table focus — a discriminated union. The `kind: 'viewer'` arm is the
 * default: the full designated-Viewer projection (the "Player View on the
 * shared screen"). The `entity` / `handout` arms PIN a single surface; both
 * still render through the designated Viewer's projection (the designated-
 * Viewer rule), so a pin only narrows WHAT is shown.
 *
 * Additive widening (deferred): a `{ kind: 'map'; depiction_id }` arm rides the
 * geography wave; `{ kind: 'hud' }` / `{ kind: 'book' }` ride #225. New arms
 * never alter the existing ones.
 */
export type TableFocus =
  | { kind: 'viewer' }
  | { kind: 'entity'; entity_id: string }
  | { kind: 'handout'; asset_id: string };

/** The default focus — the full designated-Viewer projection. */
export const DEFAULT_TABLE_FOCUS: TableFocus = { kind: 'viewer' };

/**
 * The GM designated Viewer for the table, defaulting to the **party** Viewer.
 * A party Viewer is not yet modelled in the core pack (no `party` trait), so
 * the practical default is the first campaign Viewer (a Character viewer);
 * `null` when the campaign has no selectable Viewer yet (the table then renders
 * its empty state — never GM-truth). The GM may override via the picker.
 *
 * Pure + deterministic: `campaignViewers` already sorts by label, so the first
 * entry is stable across rebuilds. Returns `null` (not the GM) when empty — the
 * table must NEVER fall back to GM-truth; an empty table is correct, a
 * GM-omniscient table is a reveal leak.
 */
export function defaultDesignatedViewer(
  entities: Record<string, EntityState>,
): ViewerId | null {
  const viewers = campaignViewers(entities);
  return viewers.length > 0 ? viewers[0]!.viewer : null;
}

/**
 * Resolve the effective designated Viewer: an explicit GM override when it is
 * still a valid, non-GM campaign Viewer, else the default. A GM override is
 * rejected (collapses to the default) — the table renders a Viewer's
 * projection, never GM-truth. A stale override (the Character was soft-deleted)
 * also falls back to the default so the table never points at a gone Viewer.
 */
export function resolveDesignatedViewer(
  override: ViewerId | null,
  entities: Record<string, EntityState>,
): ViewerId | null {
  if (override === null || override.kind === 'gm') {
    return defaultDesignatedViewer(entities);
  }
  const key = `${override.kind}:${override.id}`;
  for (const opt of campaignViewers(entities)) {
    if (`${opt.viewer.kind}:${opt.viewer.id}` === key) return override;
  }
  return defaultDesignatedViewer(entities);
}

/**
 * The pure focus reducer — fold a focus action into the next focus value.
 * Separated from the app reducer so the focus state machine is unit-testable in
 * isolation. "Pin to Table" sets an `entity` / `handout` focus; "un-pin"
 * (`{ kind: 'unpin' }`) returns focus to the viewer default. Re-pinning the
 * SAME target is a no-op-equivalent (idempotent); pinning a different target
 * replaces the focus (only one pin at a time).
 */
export type TableFocusAction =
  | { kind: 'pin-entity'; entity_id: string }
  | { kind: 'pin-handout'; asset_id: string }
  | { kind: 'unpin' };

export function reduceTableFocus(
  _current: TableFocus,
  action: TableFocusAction,
): TableFocus {
  switch (action.kind) {
    case 'pin-entity':
      return { kind: 'entity', entity_id: action.entity_id };
    case 'pin-handout':
      return { kind: 'handout', asset_id: action.asset_id };
    case 'unpin':
      return DEFAULT_TABLE_FOCUS;
  }
}

/** True when the two focus pointers are structurally equal. */
export function tableFocusEquals(a: TableFocus, b: TableFocus): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'entity' && b.kind === 'entity') return a.entity_id === b.entity_id;
  if (a.kind === 'handout' && b.kind === 'handout') return a.asset_id === b.asset_id;
  return true; // both 'viewer'
}

/**
 * One already-projected fact row, as broadcast to the Table tab. This is a
 * value-only projection of a `VisibleFact` (the chokepoint already ran); the
 * Table tab renders these directly and re-projects NOTHING. Mirrors the app's
 * `ProjectedFactRow` shape (a structural subset is intentional — the table only
 * needs to display values, not author conflict edits).
 */
export interface TableProjectedRow {
  subject: string;
  predicate: string;
  value: unknown;
  pending?: true;
  /**
   * #258 — a resolved signed content URL for an asset-valued row (the GM tab
   * resolves it before broadcasting; the Table tab has no replica/asset store).
   * Present only for asset / image facts whose URL is already resolved.
   */
  assetUrl?: string;
}

/**
 * #271 — The GM's ephemeral pointing cursor mirrored on the Table View.
 * Coordinates are normalised [0, 1] fractions of the Table View surface
 * (0,0 = top-left, 1,1 = bottom-right) so they are viewport-agnostic and
 * survive the Table tab having a different resolution than the GM tab.
 *
 * `null` means the pointer is not active (the GM's cursor is off the table
 * surface or attention mode is not engaged).
 */
export interface TableAttentionPointer {
  /** Normalised x position in [0, 1]. */
  x: number;
  /** Normalised y position in [0, 1]. */
  y: number;
}

/**
 * #271 — An ephemeral "Look at this" ripple/halo the GM triggers on the Table
 * View. Coordinates are normalised [0, 1] fractions of the Table View surface
 * (same convention as `TableAttentionPointer`). A `null` means no active ripple.
 *
 * The ripple auto-fades after `TABLE_RIPPLE_TTL_MS` on the Table tab; the GM
 * tab clears it from the model after the same interval so a second broadcast
 * with `null` is not required.
 */
export interface TableAttentionRipple {
  /** Normalised x position in [0, 1]. */
  x: number;
  /** Normalised y position in [0, 1]. */
  y: number;
  /** Monotonically increasing sequence number so identical-coordinate re-triggers are detected. */
  seq: number;
}

/** The Table View ripple auto-fades after this many ms (CSS + JS in sync). */
export const TABLE_RIPPLE_TTL_MS = 3000;

/**
 * The wire contract broadcast over `BroadcastChannel` from the GM main tab to
 * the Table tab. JSON-serialisable by construction (the channel structured-
 * clones it; we keep it to plain JSON so it round-trips through `JSON.stringify`
 * in tests and never carries a class instance). Versioned so a future widening
 * can negotiate.
 *
 * `rows` are the designated-Viewer projection — already chokepoint-filtered. A
 * `viewerLabel` rides along so the Table tab can show "Showing: <Viewer>"
 * without access to the entities map. `focus` is the ephemeral pointer.
 * `designatedViewerNull` signals the no-Viewer empty state (the table renders
 * its placeholder, never GM-truth).
 *
 * #271 — `attentionPointer` and `attentionRipple` carry the ephemeral attention
 * signals. Both are optional (absent when null) to keep the wire format terse.
 * They are arm-agnostic (coordinates on the table surface) so they compose
 * transparently with any future focus arm (map, book, etc.).
 */
export interface TableBroadcast {
  v: 1;
  kind: 'table-update';
  focus: TableFocus;
  designatedViewerNull: boolean;
  viewerLabel: string;
  rows: TableProjectedRow[];
  /**
   * #271 — The GM's live pointing cursor, normalised to [0,1]×[0,1] on the
   * Table surface. Absent when the pointer is not active.
   */
  attentionPointer?: TableAttentionPointer;
  /**
   * #271 — The active "Look at this" ripple trigger. Absent when no ripple is
   * active. The Table tab CSS animation fires on `seq` change (keyed remount).
   */
  attentionRipple?: TableAttentionRipple;
}

/** A Table tab announcing it is ready, so the GM tab re-broadcasts the state. */
export interface TableHelloBroadcast {
  v: 1;
  kind: 'table-hello';
}

export type TableChannelMessage = TableBroadcast | TableHelloBroadcast;

/** The same-origin BroadcastChannel name shared by the GM tab and Table tab. */
export const TABLE_CHANNEL_NAME = 'dl-table-view';

/**
 * Serialise a broadcast to a JSON string (the canonical wire form). The channel
 * itself accepts structured-clone, but serialising through JSON keeps the
 * contract honestly JSON-safe and gives tests a stable assertion surface.
 */
export function serialiseTableBroadcast(msg: TableChannelMessage): string {
  return JSON.stringify(msg);
}

/**
 * Parse + validate a broadcast message. Returns `null` for anything that is not
 * a well-formed, version-1 message of a known kind — the Table tab ignores
 * malformed traffic rather than throwing (a stray same-origin message must not
 * crash the shared display).
 */
export function parseTableBroadcast(raw: unknown): TableChannelMessage | null {
  let obj: unknown = raw;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (typeof obj !== 'object' || obj === null) return null;
  const m = obj as Record<string, unknown>;
  if (m['v'] !== 1) return null;
  if (m['kind'] === 'table-hello') {
    return { v: 1, kind: 'table-hello' };
  }
  if (m['kind'] !== 'table-update') return null;
  if (!isTableFocus(m['focus'])) return null;
  if (typeof m['designatedViewerNull'] !== 'boolean') return null;
  if (typeof m['viewerLabel'] !== 'string') return null;
  if (!Array.isArray(m['rows'])) return null;
  const rows: TableProjectedRow[] = [];
  for (const r of m['rows']) {
    if (typeof r !== 'object' || r === null) return null;
    const row = r as Record<string, unknown>;
    if (typeof row['subject'] !== 'string' || typeof row['predicate'] !== 'string') {
      return null;
    }
    const out: TableProjectedRow = {
      subject: row['subject'],
      predicate: row['predicate'],
      value: row['value'],
    };
    if (row['pending'] === true) out.pending = true;
    if (typeof row['assetUrl'] === 'string') out.assetUrl = row['assetUrl'];
    rows.push(out);
  }
  const result: TableBroadcast = {
    v: 1,
    kind: 'table-update',
    focus: m['focus'] as TableFocus,
    designatedViewerNull: m['designatedViewerNull'] as boolean,
    viewerLabel: m['viewerLabel'] as string,
    rows,
  };
  // #271 — optional ephemeral attention signals. Silently ignored if malformed
  // (a stray partial message must not crash the Table display).
  const ptr = parseAttentionPointer(m['attentionPointer']);
  if (ptr !== null) result.attentionPointer = ptr;
  const ripple = parseAttentionRipple(m['attentionRipple']);
  if (ripple !== null) result.attentionRipple = ripple;
  return result;
}

/** Structural guard for a `TableFocus` value arriving over the wire. */
function isTableFocus(value: unknown): value is TableFocus {
  if (typeof value !== 'object' || value === null) return false;
  const f = value as Record<string, unknown>;
  if (f['kind'] === 'viewer') return true;
  if (f['kind'] === 'entity') return typeof f['entity_id'] === 'string';
  if (f['kind'] === 'handout') return typeof f['asset_id'] === 'string';
  return false;
}

/** #271 — Parse an attention pointer from an unknown wire value, or return null. */
function parseAttentionPointer(value: unknown): TableAttentionPointer | null {
  if (typeof value !== 'object' || value === null) return null;
  const v = value as Record<string, unknown>;
  if (typeof v['x'] !== 'number' || typeof v['y'] !== 'number') return null;
  return { x: v['x'], y: v['y'] };
}

/** #271 — Parse an attention ripple from an unknown wire value, or return null. */
function parseAttentionRipple(value: unknown): TableAttentionRipple | null {
  if (typeof value !== 'object' || value === null) return null;
  const v = value as Record<string, unknown>;
  if (typeof v['x'] !== 'number' || typeof v['y'] !== 'number' || typeof v['seq'] !== 'number') {
    return null;
  }
  return { x: v['x'], y: v['y'], seq: v['seq'] };
}

/**
 * #271 — The attention signal reducer: fold a pointer-move or ripple-trigger
 * action into the ephemeral attention state carried on the broadcast. Kept pure
 * so reducer arms in `app.ts` stay unit-testable.
 *
 * `pointer-move` sets the normalised pointer position (GM tab only; x/y are
 * in [0,1]). `pointer-clear` removes the pointer (cursor left the table surface
 * or attention mode was disabled). `ripple-trigger` starts a "Look at this" halo
 * at (x, y); a monotonically increasing `seq` lets the Table tab detect
 * re-triggers at the same position.
 */
export type TableAttentionAction =
  | { kind: 'pointer-move'; x: number; y: number }
  | { kind: 'pointer-clear' }
  | { kind: 'ripple-trigger'; x: number; y: number; seq: number };

/**
 * #271 — Fold an attention action into the current broadcast fields and return
 * updated optional fields. Undefined means "no change / clear".
 */
export function reduceTableAttention(
  currentPointer: TableAttentionPointer | undefined,
  currentRipple: TableAttentionRipple | undefined,
  action: TableAttentionAction,
): { pointer: TableAttentionPointer | undefined; ripple: TableAttentionRipple | undefined } {
  switch (action.kind) {
    case 'pointer-move':
      return {
        pointer: { x: action.x, y: action.y },
        ripple: currentRipple,
      };
    case 'pointer-clear':
      return { pointer: undefined, ripple: currentRipple };
    case 'ripple-trigger':
      return {
        pointer: currentPointer,
        ripple: { x: action.x, y: action.y, seq: action.seq },
      };
  }
}

/**
 * The subset of projected rows relevant to the current focus:
 *   - `viewer` → all rows (the full Player View on the shared screen).
 *   - `entity` → only rows whose `subject` is the pinned entity.
 *   - `handout` → only the asset fact(s) whose value references the pinned
 *     asset (the handout image), filtered by `asset_id` match.
 * Always operates on rows that already passed the chokepoint, so this can never
 * widen visibility — at most it narrows the already-revealed set.
 */
export function rowsForFocus(
  focus: TableFocus,
  rows: readonly TableProjectedRow[],
): TableProjectedRow[] {
  if (focus.kind === 'viewer') return [...rows];
  if (focus.kind === 'entity') {
    return rows.filter((r) => r.subject === focus.entity_id);
  }
  // handout — match rows whose value carries the asset id (asset value kind
  // stores an `{ asset_id }` object or the bare id string).
  return rows.filter((r) => assetIdOf(r.value) === focus.asset_id);
}

/** Extract an asset id from a projected fact value (object `{asset_id}` or bare id). */
export function assetIdOf(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null) {
    const v = value as Record<string, unknown>;
    if (typeof v['asset_id'] === 'string') return v['asset_id'];
    if (typeof v['id'] === 'string') return v['id'];
  }
  return null;
}
